import type { RestoreJobInitiationResponse } from '@repo/api-gw-sdk';

import type {
  UnifiedRestorePlan,
  UnifiedRestoreRequest,
} from '@/components/restore/unifiedIdentityRestore/unifiedIdentityRestoreTypes';

import type { HttpClient } from './httpClient';
import { isMPAInterceptedResponse } from './mpaIntercept';

interface SwrResult<T> {
  body: T | undefined;
  isLoading: boolean;
  error: unknown;
  mutate: () => Promise<unknown>;
}

// The unified Entra ID + Microsoft 365 restore has no generated SDK yet, so both
// calls go through the raw httpClient against hand-built URLs. The two paths below
// are the contract the gateway is expected to serve; swap to generated Api classes
// once the domain lands in @repo/api-gw-sdk.
//
// Only the mock routes answer them today, which is why every caller sits behind
// `useUnifiedIdentityRestoreEnabled`.

const isUnifiedRestorePlan = (value: unknown): value is UnifiedRestorePlan =>
  !!value &&
  typeof value === 'object' &&
  'entities' in value &&
  Array.isArray(value.entities);

const errorMessageOf = (value: unknown): string | undefined => {
  if (!value || typeof value !== 'object' || !('message' in value)) {
    return undefined;
  }
  return typeof value.message === 'string' && value.message
    ? value.message
    : undefined;
};

const startedJobId = (value: unknown): string | undefined => {
  if (!value || typeof value !== 'object' || !('jobId' in value)) {
    return undefined;
  }
  return typeof value.jobId === 'string' && value.jobId
    ? value.jobId
    : undefined;
};

export const unifiedIdentityRestore = (
  httpClient: HttpClient,
  currentProjectId: string
) => {
  const snapshotRoot = (resourceId: string, snapshotId: string) =>
    `/projects/${currentProjectId}/resources/${resourceId}/snapshots/${snapshotId}`;

  return {
    /**
     * Resolves what the backups can say about each selected entity — its
     * inferred directory presence, and which Microsoft 365 snapshots hold which
     * of the selected users — in one call. Restore accounts are not part of the
     * response; they come from the shared restore-accounts API. Pass a null
     * `resourceId` to hold the request.
     */
    usePlan: (
      resourceId: string | null,
      snapshotId: string | undefined,
      entityIds: string[]
    ): SwrResult<UnifiedRestorePlan> => {
      const entityQuery = entityIds
        .map((entityId) => encodeURIComponent(entityId))
        .join(',');
      const url =
        resourceId && snapshotId && entityIds.length > 0
          ? `${snapshotRoot(resourceId, snapshotId)}/unified-identity-restore-plan?entityIds=${entityQuery}`
          : null;
      const result: SwrResult<unknown> = httpClient.get(url, {
        shouldRetryOnError: false,
      });

      // The shared fetch resolves on any status, so an error body arrives here
      // looking like a plan. Callers read `entities` straight away, so one that
      // has no entity list is reported as the failure it is rather than handed
      // on to throw mid-render.
      if (isUnifiedRestorePlan(result.body)) {
        return { ...result, body: result.body };
      }
      return {
        ...result,
        body: undefined,
        error: result.error ?? result.body,
      };
    },

    restore: async (
      resourceId: string,
      snapshotId: string,
      payload: UnifiedRestoreRequest
    ): Promise<RestoreJobInitiationResponse> => {
      const response: unknown = await httpClient.post(
        `${snapshotRoot(resourceId, snapshotId)}/restore-entra-id-with-microsoft-365`,
        payload
      );

      // An approval policy answers with no job id and no error. Reporting that as
      // started would be false — this path is a raw fetch, so it never ran the
      // middleware that submits the approval request, and nothing is pending
      // either. Said plainly instead of guessed at; MPA support arrives with the
      // generated client (EON-19912).
      if (isMPAInterceptedResponse(response)) {
        throw new Error(
          'This restore needs approval, which is not available from this flow yet.'
        );
      }

      // Otherwise a reply carrying no job id is a failure. Without this check a
      // rejected restore reads as a started one: the wizard would show its
      // success message, close, and clear the operator's selection for a job
      // that does not exist.
      const jobId = startedJobId(response);
      if (!jobId) {
        throw new Error(
          errorMessageOf(response) ?? 'The restore could not be started.'
        );
      }
      return { jobId };
    },
  };
};
